import { hslToRgb, rgbToHsl, type RGBA, type HSLA } from './color';
import { t } from './i18n';

export type SchemeType =
  | 'complement'
  | 'triadic'
  | 'analogous'
  | 'split-complement'
  | 'shades'
  | 'tints'
  | 'tones';

export function schemeLabel(key: SchemeType): string {
  const map: Record<SchemeType, string> = {
    complement: t('Complement'),
    triadic: t('Triadic'),
    analogous: t('Analogous'),
    'split-complement': t('Split Comp'),
    shades: t('Shades'),
    tints: t('Tints'),
    tones: t('Tones'),
  };
  return map[key];
}

/**
 * 基础配色方案（不包含主色）
 */
function rotateHue(base: HSLA, degrees: number): HSLA {
  return { ...base, h: ((base.h + degrees) % 360 + 360) % 360 };
}

export function generateScheme(base: RGBA, type: SchemeType): RGBA[] {
  const hsl = rgbToHsl(base);
  switch (type) {
    case 'complement':
      return [hslToRgb(rotateHue(hsl, 180))];
    case 'triadic':
      return [
        hslToRgb(rotateHue(hsl, 120)),
        hslToRgb(rotateHue(hsl, 240))
      ];
    case 'analogous':
      return [
        hslToRgb(rotateHue(hsl, -30)),
        hslToRgb(rotateHue(hsl, 30))
      ];
    case 'split-complement':
      return [
        hslToRgb(rotateHue(hsl, 150)),
        hslToRgb(rotateHue(hsl, 210))
      ];
    case 'shades':
      // 同色相，更暗（7 档：-10 到 -60）
      return [-10, -20, -30, -40, -50, -60, -70].map((dl) =>
        hslToRgb({ ...hsl, l: Math.max(5, hsl.l + dl) })
      );
    case 'tints':
      // 同色相，更亮（7 档：+10 到 +70）
      return [10, 20, 30, 40, 50, 60, 70].map((dl) =>
        hslToRgb({ ...hsl, l: Math.min(95, hsl.l + dl) })
      );
    case 'tones':
      // 同色相，灰度（7 档）
      return [-15, -30, -45, 0, 15, 30, 45].map((ds) =>
        hslToRgb({ ...hsl, s: Math.max(0, Math.min(100, hsl.s + ds)) })
      );
  }
}
